<template>
    <div v-if="visible" style="display: flex; max-width: calc(100vw - 75px)">
        <div
            v-show="chatBarCurrentPage > 1"
            class="custom-chatbar-button"
            :class="!isUnreadPage(chatBarCurrentPage - 1) && 'none-unread'"
            @click="setChatBarCurrentPageChange(chatBarCurrentPage - 1)"
        >
            <md-icon>keyboard_arrow_left</md-icon>
        </div>

        <div
            v-for="(b, j) in chatBarPaginationList()"
            v-if="j === chatBarCurrentPage - 1"
            id="custom-chatbar"
            style="margin-right: auto"
        >
            <div v-for="(c, i) in b" :key="i" class="item">
                <div
                    :class="'short-key ' + getChatBarColorClassNames(c)"
                    v-if="!!c"
                    @click="toggle(c)"
                    :style="{ transform: animation[c._id] ? 'scale(1.5)' : 'scale(1)' }"
                >
                    {{ i + 1 }}
                </div>
                <div class="custom-chatbar-box" data-v-step="22">
                    <div @click="toggle(c)">
                        <div class="name">
                            {{ capitalize(c.fullname || c.whatsappNumber) }}
                        </div>
                        <div class="time">
                            <passed-time-counter-ago :id="c._id" :date="c.createdDate" />
                        </div>
                        <b-badge v-if="c.meta.unread">
                            {{ c.meta.unread }}
                        </b-badge>
                    </div>

                    <div v-if="!activeConversation" class="toolbox">
                        <a-tooltip v-if="activeConversation && activeConversation._id === c._id">
                            <template slot="title">
                                {{ $t("message.hideChat") }}
                            </template>
                            <button @click.prevent="hide">
                                <font-awesome-icon icon="window-minimize" />
                            </button>
                        </a-tooltip>
                        <a-tooltip v-if="isRoot || user.role.permissions.map((x) => x.name).includes('visitor.block')">
                            <template slot="title">
                                {{ $t("message.block_") }}
                            </template>
                            <button @click.prevent="ban(c._id, c.fullname)">
                                <font-awesome-icon icon="ban" style="color: orange" />
                            </button>
                        </a-tooltip>
                        <a-tooltip>
                            <template slot="title">
                                {{ $t("message.privateMessage_") }}
                            </template>
                            <button @click.prevent="pm(c._id)">
                                <font-awesome-icon icon="user-secret" style="color: dodgerblue" />
                            </button>
                        </a-tooltip>
                        <a-tooltip
                            v-if="activeConversation && activeConversation._id === c._id && !observing.includes(c._id)"
                        >
                            <template slot="title">
                                {{ $t("message.assignRep") }}
                            </template>
                            <button v-b-modal.assign-agent>
                                <font-awesome-icon icon="headset" />
                            </button>
                        </a-tooltip>
                        <a-tooltip>
                            <template slot="title">
                                {{
                                    c.users.length > 1 || observing.includes(c._id)
                                        ? $t("message.leaveChat")
                                        : $t("message.endChat")
                                }}
                            </template>
                            <button @click.prevent="leave(c._id, true)">
                                <font-awesome-icon
                                    v-if="c.users.length > 1 || observing.includes(c._id)"
                                    icon="sign-out-alt"
                                    style="color: red"
                                />
                                <font-awesome-icon
                                    v-else-if="c.users.length === 1 || observing.includes(c._id)"
                                    icon="times"
                                    style="color: red"
                                />
                            </button>
                        </a-tooltip>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-show="chatBarPaginationList().length > chatBarCurrentPage"
            class="custom-chatbar-button"
            :class="!isUnreadPage(chatBarCurrentPage + 1) && 'none-unread'"
            @click="setChatBarCurrentPageChange(chatBarCurrentPage + 1)"
        >
            <md-icon>keyboard_arrow_right</md-icon>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import PassedTimeCounterAgo from "./PassedTimeCounterAgo";
import { log2 } from "../utils/log2";
import { capitalize, fakeConversation } from "../utils/utils";
import * as _ from "lodash";
import { socketMessageStream } from "../utils/socketStream";
import { filter, bufferTime, map } from "rxjs/operators";

const log = log2("ChatBar");
export default {
    name: "ChatBar",
    components: { PassedTimeCounterAgo },
    props: {
        visible: {
            type: Boolean,
            default: true,
        },
        returnRef: {
            type: Function,
        },
    },
    computed: {
        ...mapState({
            lookedChats: (state) => state.chat.lookedConversationIds,
            observing: (state) => state.chat.observing,
            activeConversation: (state) => state.chat.activeConversation,
            menu: (state) => state.layout.menu,
            settings: (state) => state.setting.settings,
            tabVisible: (state) => state.app.tabVisible,
            user: (state) => state.auth.user,
            isRoot: (state) => state.auth.user.role.isRoot,
            chatBarCurrentPage: (state) => state.app.chatBarCurrentPage,
            chatBarPageSize: (state) => state.app.chatBarPageSize,
            liveVisitors: (state) => state.visitor.liveVisitors,
            fakeMode: (state) => state.app.fakeMode,
            conversationsMeta: (state) => state.chat.conversationsMeta,
            getUnreadConversations: (state) => {
                return state.chat.conversations.filter((c) => {
                    const meta = state.chat.conversationsMeta[c._id] || {};
                    return meta.unread > 0;
                });
            },
        }),
        ...mapGetters({
            myActiveConversations: "chat/getMyActiveConversations",
            findConversation: "chat/findConversation",
        }),

        rows() {
            return this.myActiveConversations.length;
        },
        currentPage: {
            get() {
                return this.chatBarCurrentPage;
            },
            set(value) {
                this.setChatBarCurrentPageChange(value);
            },
        },
    },
    data() {
        return {
            cancels: [],
            animation: {},
        };
    },
    methods: {
        ...mapActions({
            endConversation: "chat/endConversation",
            joinConversation: "chat/joinConversation",
            leaveConversation: "chat/leaveConversation",
            hideConversation: "chat/hideConversation",
            banConversation: "chat/banConversation",
            setPrivateMessageConversation: "chat/setPrivateMessageConversation",
            setChatBarCurrentPageChange: "app/setChatBarCurrentPageChange",
            setChatBarPageSize: "app/setChatBarPageSize",
        }),
        capitalize,
        isUnreadPage(page) {
            page -= 1;
            let list = this.chatBarPaginationList();
            if (page > list.length - 1 || page < 0) return false;
            return this.chatBarPaginationList()[page].some((x) =>
                this.getUnreadConversations.map((i) => i._id).includes(x._id)
            );
        },
        log(str) {
            log(str);
        },
        IsVisitorOnline(conversation, visitorIdOrObject) {
            if (conversation.channel) return true;
            const isObj = typeof visitorIdOrObject === "object";
            const visitorId = isObj ? visitorIdOrObject._id : visitorIdOrObject;
            const isWhatsappVisitor = !!_.get(visitorIdOrObject, "whatsappNumber");
            const isInstagramVisitor = !!_.get(visitorIdOrObject, "instagramUserId");
            const isTelegramVisitor = !!_.get(visitorIdOrObject, "telegramUserId");
            if (isWhatsappVisitor || isInstagramVisitor || isTelegramVisitor) return true;
            else return this.liveVisitors.includes(visitorId);
        },
        chatBarPaginationList() {
            let allItems = this.fakeMode ? [fakeConversation()] : this.myActiveConversations;
            let widthScreen = window.screen.width;
            if (widthScreen < 1180) {
                allItems = _.chunk(allItems, 7);
                this.setChatBarPageSize(7);
            } else {
                allItems = _.chunk(allItems, 9);
                this.setChatBarPageSize(9);
            }
            return allItems;
        },
        onChange() {
            this.hideConversation();
        },
        getChatBarColorClassNames(c) {
            const activeConversationId = this.activeConversation?._id;
            let classNames = [];
            if (c.meta.unread > 0 && !this.observing.includes(c._id)) {
                classNames = ["unread"];
            } else if (activeConversationId === c._id) {
                classNames = ["active", "selected"];
            } else if (!this.findConversation(c._id) || !this.IsVisitorOnline(c, c.visitor)) {
                classNames = ["passive"];
            } else if (!this.lookedChats.includes(c._id)) {
                classNames = ["auto-assign"];
            } else if (this.observing.includes(c._id) || c.status === "destroyed") {
                classNames = ["observing"];
            }
            return classNames.join(" ");
        },
        hide() {
            this.hideConversation();
        },
        toggle(conversation) {
            const activeConversationId = this.activeConversation?._id;
            if (!this.activeConversation || conversation._id !== activeConversationId) {
                this.joinConversation({
                    conversation,
                    observe: this.observing.includes(conversation._id),
                    sendToSocket: false,
                });
            } else {
                this.hideConversation();
            }
        },
        leave(id, noConfirm = false) {
            const conservation = this.findConversation(id);
            if (conservation) {
                if (conservation.users.length === 1 && !this.observing.includes(conservation._id)) {
                    this.end(conservation._id);
                } else {
                    this.leaveConversation(conservation._id);
                }
            }
        },
        end(id) {
            this.$dialog
                .confirm(this.$t("message.endChatConfirm"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.endConversation(id);
                });
        },
        ban(id, name) {
            this.$dialog
                .confirm(this.$t("message.blockUserConfirm").concat(` -  ${name}`), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.banConversation(id);
                });
        },
        pm(id) {
            this.setPrivateMessageConversation(id);
        },
        afterChange(current) {
            this.setChatBarCurrentPageChange(current + 1);
        },
    },
    mounted() {
        if (this.returnRef) this.returnRef(this.$refs.carouselRef);
        const sub = socketMessageStream.pipe(filter((x) => ["message"].includes(x.cmd))).subscribe((x) => {
            if (["visitor", "user"].includes(x.data.messageType)) {
                this.$set(this.animation, x.data.conversationId, true);
                setTimeout(() => (this.animation[x.data.conversationId] = false), 500);
            }
        });
        this.cancels.push(() => sub.unsubscribe());
    },
    beforeDestroy() {
        this.cancels.forEach((x) => x());
    },
};
</script>

<style lang="scss" scoped>
.new-theme {
    .custom-chatbar-button {
        padding: 10px 0;
        cursor: pointer;
        background-color: #4e8cff;
        border-radius: 6px;
        margin: 0 10px;
        box-shadow: 0 0 3px #00000060;

        i {
            color: white;
        }

        &.none-unread {
            background-color: white;

            i {
                color: black;
            }
        }
    }

    #custom-chatbar {
        padding: 0 15px;
        display: flex !important;

        .item {
            height: 40px;
            display: flex;
            user-select: none;
            cursor: pointer;
            position: relative;
            margin-right: 4px;

            &:last-child {
                margin-right: 0;
            }

            .short-key {
                transition: all 0.3s;
                background-color: #ffffff;
                color: #000000;
                min-width: 30px;
                display: flex;
                justify-content: center;
                align-items: center;
                border-radius: 6px;
                margin-right: 2px;
                font-size: 18px;
                font-family: Poppins;
                font-weight: 500;
                box-shadow: 0 0 3px #00000080;
                cursor: pointer;

                &.unread {
                    background-color: #3699ff;
                    color: #ffffff;
                }

                &.active {
                    background-color: #1bc5bd;
                    color: #ffffff;
                }

                &.passive {
                    background-color: #afafaf;
                    color: #ffffff;
                }

                &.auto-assign {
                    background-color: #ffa800;
                    color: #ffffff;
                }

                &.observing {
                    background-color: #f64e60;
                    color: #ffffff;
                }
            }

            .custom-chatbar-box {
                background-color: #ffffff;
                display: flex;
                flex-direction: column;
                justify-content: center;
                padding: 0 8px;
                border-radius: 6px;
                width: 82px;
                overflow: hidden;
                box-shadow: 0 0 3px #00000080;

                .name {
                    font-family: Poppins;
                    font-size: 10px;
                    font-weight: 600;
                    white-space: nowrap;
                    display: -webkit-box;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    -webkit-line-clamp: 1;
                    text-transform: capitalize;
                }

                .time {
                    font-family: Poppins;
                    font-size: 10px;
                    font-weight: 300;
                    white-space: nowrap;
                }

                span.badge {
                    position: absolute;
                    top: 0;
                    right: 0;
                    padding: 4px;
                }
            }

            .toolbox {
                position: absolute;
                top: -30px;
                left: 32px;
                right: 0;
                display: flex;
                justify-content: space-between;
                transition: all 0.3s;
                opacity: 0;
                transform: translateY(20%);

                button {
                    background-color: #ffffff;
                    padding: 0;
                    border: none;
                    width: 22px;
                    height: 22px;
                    border-radius: 5px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 0 3px #00000080;
                    font-size: 12px;
                }
            }

            &:hover {
                .toolbox {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
        }
    }

    .pagination {
        position: absolute;
        top: 0;
        right: 100px;
    }
}
</style>

<style lang="scss">
.ant-carousel {
    height: 80px;
    width: 100vw;
    margin-bottom: 30px;

    .slick-list {
        padding-top: 35px;
        padding-bottom: 10px;
    }

    span.dot {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: #0e4d73;
        margin-top: 20px;
    }

    .slick-active span.dot {
        background-color: #0cbaba;
    }
}

.ant-carousel .slick-list .slick-slide {
    pointer-events: auto;
}
</style>
